import { cloneElement, ReactElement, ReactNode, useRef, useState } from 'react';
import { Dialog, Heading, PopoverProps } from 'react-aria-components';
import { ClarionBasePopover } from './ClarionBasePopover';

export type ClarionPopoverProps = PopoverProps & {
  // Must support forwardRef or pass ref as a prop
  target: ReactElement<any>;
  // Either title or aria-label must be provided
  title?: string;
  children: ReactNode;
};

function ClarionControlledPopover({
  target,
  children,
  className,
  'aria-label': ariaLabel,
  title,
  isOpen,
  onOpenChange,
  ...props
}: ClarionPopoverProps) {
  const targetRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const clonedTarget = cloneElement(target, {
    ref: targetRef,
    onClick: (e: React.MouseEvent) => {
      target.props.onClick?.(e);
      onOpenChange?.(!isOpen);
    },
  });

  return (
    <>
      {clonedTarget}
      <ClarionBasePopover
        {...props}
        triggerRef={targetRef}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        scrollRef={scrollRef}
        showArrow
      >
        <Dialog
          aria-label={ariaLabel}
          className="outline-hidden! p-4 overflow-auto max-h-[inherit]"
          ref={scrollRef}
        >
          {title && (
            <Heading slot="title" className="text-lg font-semibold mb-2">
              {title}
            </Heading>
          )}
          {children}
        </Dialog>
      </ClarionBasePopover>
    </>
  );
}

export default function ClarionPopover(props: ClarionPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  // Consumer-controlled mode
  if (typeof props.onOpenChange === 'function') {
    return <ClarionControlledPopover {...props} />;
  }
  // Uncontrolled mode
  return <ClarionControlledPopover {...props} isOpen={isOpen} onOpenChange={setIsOpen} />;
}
